import type { EmailActivity } from '@common/types/activities';
import { Avatar, Box, Link, type SxProps, type Theme, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { convertDateFormat, convertToHtml, truncate } from '@/utils';
import ApprovalEmailBody from './bodies/ApprovalEmailBody';
import ClientDeclineEmailBody from './bodies/ClientDeclineEmailBody';
import DefaultEmailBody from './bodies/DefaultEmailBody';
import IntakeEmailBody from './bodies/IntakeEmailBody';
import IntakeReminderBody from './bodies/IntakeReminderBody';
import IntakeReminderExhaustToLawyerBody from './bodies/IntakeReminderExhaustToLawyerBody';
import LawyerDeclineEmailBody from './bodies/LawyerDeclineEmailBody';
import PostIntakeEmailBody from './bodies/PostIntakeEmailBody';
import QuoteApprovalToClientBody from './bodies/QuoteApprovalToClientBody';
import QuoteEmailReminderBody from './bodies/QuoteEmailReminderBody';
import QuoteEmailToLawyerForApprovalBody from './bodies/QuoteEmailToLawyerForApprovalBody';
import QuoteReminderExhaustToLawyerBody from './bodies/QuoteReminderExhaustToLawyerBody';
import RequestACallBody from './bodies/RequestACallBody';
import TreefortIdvCompletionMailToLawyerBody from './bodies/TreefortIdvCompletionMailToLawyerBody';
import { getNameInitials } from './util';

interface EmailSummaryCardProps {
    activity: EmailActivity;
}

export default function EmailSummaryCard({ activity }: EmailSummaryCardProps) {
    const theme = useTheme();
    const [isBodyExpanded, setIsBodyExpanded] = useState<boolean>(false);

    const emailSummaryCardWrapperStyles: SxProps<Theme> = {
        borderTop: `1px solid ${theme.palette.neutral.borderColor}`,
        width: '100%',
        padding: '16px 0px',
    };

    const contentGridStyles: SxProps<Theme> = {
        display: 'grid',
        gridTemplateColumns: '40px 1fr 165px',
        gap: '16px',
    };

    const subHeadings = {
        QuoteEmailToLawyerForApproval: 'Approval required',
        IntakeEmail: '',
        IntakeEmailReminderExhaustNotificationToLawyer: 'Intake reminders limit exhaust',
        QuoteEmailReminderExhaustNotificationToLawyer: 'Quote reminders limit exhaust',
        IntakeReminder: 'Intake email reminder',
        PostIntakeEmail: 'Post intake email',
        TreefortIdvCompletionMailToLawyer: 'Identity Verification Successfully Completed',
        default: 'Quote request response',
    };

    const getCollapseContent = (activityEmailType: string, emailBody: EmailActivity['body']) => {
        const startingContents: Record<string, string | undefined> = {
            ClientDeclineEmail: `${emailBody?.ClientName} has declined the quote with the following
      message:`,
            RequestACall: `${emailBody?.ClientName} has requested a call with the following message:`,
            ApprovalEmail: `${emailBody?.ClientName} has accepted the Quote.`,
            QuoteEmailToLawyerForApproval: `Client name: ${emailBody?.ClientName}`,
            LawyerDeclineEmail: `Dear ${emailBody?.ClientName || ''},`,
            QuoteEmailReminderExhaustNotificationToLawyer:
                'This is to inform you that the maximum number of reminders has been sent',
            IntakeEmailReminderExhaustNotificationToLawyer:
                'This is to inform you that the maximum number of reminders has been sent',
            PostIntakeEmail: `Hi ${emailBody?.ClientFirstName},`,
            default: emailBody?.EmailBodyIntroduction || emailBody?.IntakeEmailIntroduction,
        };
        if (activityEmailType === 'IntakeEmail') {
            if (emailBody?.IntakeEmailIntroduction) {
                subHeadings.IntakeEmail = 'Intake email';
                startingContents.IntakeEmail = `Hi ${
                    emailBody?.ClientName || emailBody.ClientFirstName || ''
                },`;
            } else {
                subHeadings.IntakeEmail = 'Intake form submitted';
                startingContents.IntakeEmail =
                    emailBody?.Clients && emailBody?.Clients?.length > 1
                        ? 'You received the intake form for the following clients:'
                        : `${emailBody?.ClientName} has completed the intake form.`;
            }
        } else if (activityEmailType === 'TreefortIdvCompletionMailToLawyer') {
            subHeadings.TreefortIdvCompletionMailToLawyer =
                'Identity Verification Successfully Completed';
            startingContents.TreefortIdvCompletionMailToLawyer = `${emailBody?.FirstName} has successfully completed the Identity verification process.,`;
        }

        const currStartingContent =
            startingContents[activityEmailType as keyof typeof startingContents] ??
            startingContents.default;
        const extraStarting =
            activityEmailType !== 'IntakeEmailReminderExhaustNotificationToLawyer'
                ? !startingContents[activityEmailType as keyof typeof startingContents] &&
                  `Hello ${emailBody?.ClientName},`
                : '';

        return {
            subHeading:
                subHeadings[activityEmailType as keyof typeof subHeadings] ?? subHeadings.default,
            startingContent: truncate(currStartingContent || '', 50),
            extraStarting,
        };
    };

    const renderEmailBody = (activityEmailType: string, emailBody: EmailActivity['body']) => {
        // When body is not expanded
        if (!isBodyExpanded) {
            return (
                <Box sx={{ marginTop: '16px' }}>
                    <Typography
                        variant="h4"
                        sx={{
                            marginBottom: '16px',
                        }}
                    >
                        {getCollapseContent(activityEmailType, emailBody).subHeading}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.primary"
                        dangerouslySetInnerHTML={{
                            __html: convertToHtml(
                                getCollapseContent(activityEmailType, emailBody).extraStarting ||
                                    '',
                            ),
                        }}
                    ></Typography>
                    <Typography
                        variant="body2"
                        color="text.primary"
                        dangerouslySetInnerHTML={{
                            __html: convertToHtml(
                                getCollapseContent(activityEmailType, emailBody).startingContent ||
                                    '',
                            ),
                        }}
                    ></Typography>
                </Box>
            );
        }

        // When body is expanded
        switch (activityEmailType) {
            case 'QuoteApprovalToClient':
                return (
                    <QuoteApprovalToClientBody
                        emailBody={emailBody}
                        isBodyExpanded={isBodyExpanded}
                        setIsBodyExpanded={setIsBodyExpanded}
                    />
                );
            case 'QuoteEmailReminder':
                return (
                    <QuoteEmailReminderBody
                        emailBody={emailBody}
                        isBodyExpanded={isBodyExpanded}
                        setIsBodyExpanded={setIsBodyExpanded}
                    />
                );
            case 'ApprovalEmail':
                return (
                    <ApprovalEmailBody
                        emailBody={emailBody}
                        isBodyExpanded={isBodyExpanded}
                        setIsBodyExpanded={setIsBodyExpanded}
                    />
                );
            case 'IntakeReminder':
                return (
                    <IntakeReminderBody
                        emailBody={emailBody}
                        isBodyExpanded={isBodyExpanded}
                        setIsBodyExpanded={setIsBodyExpanded}
                    />
                );
            case 'IntakeEmail':
                return (
                    <IntakeEmailBody
                        emailBody={emailBody}
                        isBodyExpanded={isBodyExpanded}
                        setIsBodyExpanded={setIsBodyExpanded}
                    />
                );
            case 'ClientDeclineEmail':
                return (
                    <ClientDeclineEmailBody
                        emailBody={emailBody}
                        isBodyExpanded={isBodyExpanded}
                        setIsBodyExpanded={setIsBodyExpanded}
                    />
                );
            case 'RequestACall':
                return (
                    <RequestACallBody
                        emailBody={emailBody}
                        isBodyExpanded={isBodyExpanded}
                        setIsBodyExpanded={setIsBodyExpanded}
                    />
                );
            case 'QuoteEmailToLawyerForApproval':
                return (
                    <QuoteEmailToLawyerForApprovalBody
                        emailBody={emailBody}
                        isBodyExpanded={isBodyExpanded}
                        setIsBodyExpanded={setIsBodyExpanded}
                    />
                );
            case 'LawyerDeclineEmail':
                return (
                    <LawyerDeclineEmailBody
                        emailBody={emailBody}
                        isBodyExpanded={isBodyExpanded}
                        setIsBodyExpanded={setIsBodyExpanded}
                    />
                );
            case 'QuoteEmailReminderExhaustNotificationToLawyer':
                return (
                    <QuoteReminderExhaustToLawyerBody
                        emailBody={emailBody}
                        isBodyExpanded={isBodyExpanded}
                        setIsBodyExpanded={setIsBodyExpanded}
                    />
                );
            case 'IntakeEmailReminderExhaustNotificationToLawyer':
                return (
                    <IntakeReminderExhaustToLawyerBody
                        emailBody={emailBody}
                        isBodyExpanded={isBodyExpanded}
                        setIsBodyExpanded={setIsBodyExpanded}
                    />
                );
            case 'PostIntakeEmail':
                return (
                    <PostIntakeEmailBody
                        emailBody={emailBody}
                        isBodyExpanded={isBodyExpanded}
                        setIsBodyExpanded={setIsBodyExpanded}
                    />
                );
            case 'TreefortIdvCompletionMailToLawyer':
                return (
                    <TreefortIdvCompletionMailToLawyerBody
                        emailBody={emailBody}
                        isBodyExpanded={isBodyExpanded}
                        setIsBodyExpanded={setIsBodyExpanded}
                    />
                );
            default:
                return <DefaultEmailBody emailBody={emailBody} />;
        }
    };

    // Template
    return (
        <Box sx={emailSummaryCardWrapperStyles}>
            <Box sx={contentGridStyles}>
                <Avatar sx={{ bgcolor: theme.palette.primary.P_750 }}>
                    {getNameInitials(
                        activity?.owner?.firstName || '',
                        activity?.owner?.lastName || '',
                    )}
                </Avatar>
                <Box className={'Content'}>
                    <Box>
                        <Typography variant={'body2'}>
                            From:
                            <Typography variant={'labelMedium'}>
                                <Link
                                    sx={{
                                        marginLeft: '8px',
                                        textDecoration: 'none',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {activity?.owner?.fullName}
                                </Link>
                            </Typography>
                        </Typography>
                        <Typography variant={'body2'}>
                            To:
                            <Typography variant={'labelMedium'}>
                                <Link
                                    sx={{
                                        marginLeft: '8px',
                                        textDecoration: 'none',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {activity?.recipients?.join(', ')}
                                </Link>
                            </Typography>
                        </Typography>
                    </Box>
                    <Typography
                        variant={'labelMedium'}
                        color="text.primary"
                        className="emailSubject"
                    >
                        <Typography variant="body2" display="inline">
                            Subject:{' '}
                        </Typography>
                        {activity?.body?.Subject || activity?.subject}
                    </Typography>
                    <Typography variant={'body2'} className="emailCopy"></Typography>
                    {renderEmailBody(activity.activityEmailType ?? '', activity.body)}
                    {!isBodyExpanded && (
                        <div className="actions">
                            <Link
                                onClick={() => setIsBodyExpanded(true)}
                                sx={{
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    marginTop: '8px',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                }}
                            >
                                View all
                            </Link>
                        </div>
                    )}
                </Box>
                <Typography sx={{ color: theme.palette.neutral.N_500 }}>
                    {activity?.createdAt ? convertDateFormat(activity?.createdAt) : ''}
                </Typography>
            </Box>
        </Box>
    );
}
